import { HTMLProps, PropsWithChildren } from "react";

import { clsxMerge } from "shared/lib/helpers";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";

/**
 * @param {string} containerClassName - The class name for the container which created the gradient effect.
 * @param {string} className - The class name for the component (children).
 * @param {ReactNode} children - The children of the component.
 * @param {boolean} isBorderDisabled - The flag to disable the border and turn the component into a regular wrapper for any ReactNode.
 */
interface BorderWithGradientPropsI
  extends PropsWithClassNameI, PropsWithChildren, HTMLProps<HTMLDivElement> {
  containerClassName?: string;
  isBorderDisabled?: boolean;
  showBorderOnHover?: boolean;
  borderWidthPx?: number;
  borderRadiusPx?: number;
}

// Use this component to implement the block's gradient border, which became pretty common in the latest designs.
export const BorderWithGradient = ({
  containerClassName,
  className,
  children,
  isBorderDisabled,
  showBorderOnHover = false,
  borderWidthPx = 2,
  borderRadiusPx = 4,
  ...rest
}: BorderWithGradientPropsI) => (
  <div
    className={clsxMerge(
      "bg-transparent",
      !isBorderDisabled &&
        !showBorderOnHover &&
        "overflow-hidden bg-gradient-to-r from-[#4C6192] via-[#7E46CA] to-[#CB36AD]",
      !isBorderDisabled &&
        "hover:bg-gradient-to-r hover:from-[#CB36AD] hover:via-[#7E46CA] hover:to-[#4C6192]",
      containerClassName
    )}
    style={{
      padding: `${borderWidthPx}px`,
      borderRadius: `${borderRadiusPx + borderWidthPx}px`,
    }}
    {...rest}
  >
    <div
      className={clsxMerge(
        !isBorderDisabled && "bg-white px-3 py-1",
        className
      )}
      style={{ borderRadius: `${borderRadiusPx}px` }}
    >
      {children}
    </div>
  </div>
);
